/*
 * Segment operations for the Demo Environment Management page. Datasource-level
 * entity. Mirrors sales-demo-env-setup's `src/lib/segment.js`, adapted to use
 * `CsqApiClient`. "Create" is always replicated from Source (see
 * `buildSegmentsCreateFromSourceOperations` in entities-tab.js) rather than a
 * hardcoded default query.
 *
 * NOTE on delete: no delete action was captured in the original HAR this was
 * ported from. `deleteSegment` follows the same REST resource shape as the
 * confirmed list/create calls but is INFERRED — verify with a dry run before
 * relying on it.
 */

/**
 * Fixes up "donesequence" (do-A-then-B funnel/sequence) conditions' time window before create: the
 * source datasource serializes their range as `over: { start, step, stop }`, but the create
 * endpoint's validator expects the modern `{ start, end }` range shape and throws ("Cannot use
 * 'in' operator to search for 'end' in undefined") when `end` is missing entirely, instead of
 * treating it as merely absent. Conditions can nest arbitrarily under `where`/`and`/`or`, so this
 * walks the whole query tree and, for any `over` object that has `stop` but no `end`, adds
 * `end: stop` — `stop` is already the range's upper bound (e.g. 0 = "now"), so this only supplies
 * the key the server now also expects, it doesn't change what the condition means. A "hasdone"-
 * style segment (no `over`/`stop` anywhere) round-trips through this unchanged.
 */
function withSequenceWindowEnd(node) {
  if (Array.isArray(node)) return node.map(withSequenceWindowEnd);
  if (!node || typeof node !== 'object') return node;

  const next = {};
  for (const [key, value] of Object.entries(node)) {
    next[key] = withSequenceWindowEnd(value);
  }
  if (next.over && typeof next.over === 'object' && 'stop' in next.over && !('end' in next.over)) {
    next.over = { ...next.over, end: next.over.stop };
  }
  return next;
}

/**
 * Creates a segment in a datasource.
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {string|number} datasourceId
 * @param {{ name: string, query: object }} data
 * @returns {Promise<object>} The created segment (includes `id`).
 */
export async function createSegment(client, datasourceId, { name, query }) {
  return client.post(`/api/pa-metadata/v1/projects/${datasourceId}/symbols`, { name, query: withSequenceWindowEnd(query) });
}

/**
 * Lists segments in a datasource.
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {string|number} datasourceId
 * @param {{ search?: string, take?: number, skip?: number }} [options]
 * @returns {Promise<Array<{ id: string, name: string }>>}
 */
export async function listSegments(client, datasourceId, { search = '', take = 100, skip = 0 } = {}) {
  const query = new URLSearchParams();
  query.append('order', 'updateTime desc');
  query.append('order', 'time desc');
  query.append('text', search);
  query.append('skip', String(skip));
  query.append('take', String(take));
  query.append('withCount', 'true');
  query.append('type', 'segment');

  const payload = await client.get(`/api/pa-metadata/v1/projects/${datasourceId}/symbols?${query.toString()}`);
  const items = (payload && payload.items) || [];
  return items.map((item) => ({ id: item.id, name: item.name }));
}

/**
 * Lists segments in a datasource with their full `query` definition, instead
 * of just `{id, name}`. Used to recreate a datasource's segments on another
 * datasource (see "Create from Source" in entities-tab.js).
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {string|number} datasourceId
 * @param {{ take?: number }} [options]
 * @returns {Promise<Array<{ id: string, name: string, query: object }>>}
 */
export async function listSegmentsWithQuery(client, datasourceId, { take = 1000 } = {}) {
  const query = new URLSearchParams();
  query.append('order', 'updateTime desc');
  query.append('order', 'time desc');
  query.append('text', '');
  query.append('skip', '0');
  query.append('take', String(take));
  query.append('withCount', 'true');
  query.append('type', 'segment');

  const payload = await client.get(`/api/pa-metadata/v1/projects/${datasourceId}/symbols?${query.toString()}`);
  const items = (payload && payload.items) || [];
  return items.map((item) => ({ id: item.id, name: item.name, query: item.query }));
}

/**
 * Deletes a segment by id. INFERRED endpoint — see module header comment.
 */
export async function deleteSegment(client, datasourceId, segmentId) {
  return client.del(`/api/pa-metadata/v1/projects/${datasourceId}/symbols/${segmentId}`);
}
